/** Lógica pura del albarán de recogida: qué se precarga de dónde, cómo se compone
 *  el incoterm y qué cuerpo se manda.
 *
 *  Es el único documento del cajón con formulario de verdad: once datos salen del
 *  sistema y ocho los teclea una persona. Por eso el estado de pantalla (strings)
 *  y el cuerpo que viaja (números, sin huecos) son cosas distintas, y el paso de
 *  una a otra vive aquí y tiene test. */
import type {
  AlbaranRecogidaBody,
  AlbaranRecogidaDatos,
  AlbaranRecogidaLineaEnviada,
} from '@/types'

/** Lo que la carga ya sabe en el sitio desde donde se abre el cajón. No se hace
 *  otra llamada para esto. Todo opcional: el cajón se abre también desde el
 *  pedido, que no trae cliente ni destino de la carga. */
export interface ContextoAlbaran {
  /** Primer pedido de venta de la carga. */
  pedido?: string | null
  /** Razón social del cliente. */
  destinatario?: string | null
  destino?: string | null
  /** Tipo de palet del cliente. */
  tipoPalet?: string | null
}

/** Una línea en pantalla. Todo texto: lo que se teclea no es todavía un número,
 *  y un campo vacío tiene que poder seguir siendo vacío. */
export interface AlbaranLineaForm {
  /** Clave de React; no viaja. */
  id: number
  /** Texto libre («Loose Cargo», «2 Europeos»). */
  palets: string
  bultos: string
  contenido: string
  peso: string
}

export interface AlbaranForm {
  puntoRecogida: string
  telefono: string
  correo: string
  personaEncargada: string
  fechaRecogida: string
  horario: string
  pedido: string
  tipoEnvio: string
  transportista: string
  contenedor: string
  tipoPalet: string
  tipoCarga: string
  /** El CÓDIGO del incoterm (CFR, DAP…). Dato bueno del sistema. */
  incoterm: string
  /** El puerto, que teclea la persona. Separado del código para que nadie borre
   *  el código al escribir el puerto. */
  incotermPuerto: string
  destinatario: string
  destino: string
  observaciones: string
  lineas: AlbaranLineaForm[]
}

let siguienteId = 1
export function lineaVacia(parcial: Partial<Omit<AlbaranLineaForm, 'id'>> = {}): AlbaranLineaForm {
  return { id: siguienteId++, palets: '', bultos: '', contenido: '', peso: '', ...parcial }
}

/** Número → texto de campo. `null` es un hueco, no un 0. */
function numATexto(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return ''
  return String(Math.round(n * 100) / 100)
}

/** Texto de campo → número, o `undefined` si no hay número.
 *
 *  Vacío y no numérico dan `undefined`, **nunca 0**: un peso en blanco que viaja
 *  como 0 kg es una medida que nadie va a poner en duda. Acepta coma decimal. */
export function aNumero(texto: string): number | undefined {
  const s = texto.trim().replace(/\s/g, '').replace(',', '.')
  if (s === '') return undefined
  const n = Number(s)
  return Number.isFinite(n) ? n : undefined
}

/** El incoterm tal como se imprime: código + puerto («CFR HOUSTON»).
 *
 *  El puerto NO se deduce del destino: se midió y acierta en 8 de 15, que es lo
 *  justo para engañar a quien mira una muestra («CFR AVINYONET DEL PENEDES»,
 *  «FOB CUENCA»). Lo teclea una persona. */
export function componerIncoterm(codigo: string, puerto: string): string {
  return [codigo, puerto]
    .map(p => p.trim().replace(/\s+/g, ' '))
    .filter(Boolean)
    .join(' ')
    .toUpperCase()
}

/** El formulario con lo que se precarga, de dónde:
 *
 *  - del ENDPOINT: incoterm (código), punto de recogida, transportista, tipo de
 *    envío, contenedor, y los totales de la carga en la línea inicial;
 *  - de la PROPIA CARGA (`contexto`): pedido, destinatario, destino, tipo de palet;
 *  - EN BLANCO, porque no existen en ningún sitio: fecha, horario, persona
 *    encargada, tipo de carga, observaciones, puerto, teléfono y correo.
 *
 *  La línea inicial lleva `cajas` en bultos y `pesoBruto` en peso. Si no están
 *  (`null`) quedan en blanco: nunca se rellenan con 0. `palets` va en blanco
 *  siempre. */
export function formularioInicial(
  datos: AlbaranRecogidaDatos | undefined,
  contexto: ContextoAlbaran = {},
): AlbaranForm {
  return {
    puntoRecogida: datos?.recogidaTexto ?? '',
    telefono: '',
    correo: '',
    personaEncargada: '',
    fechaRecogida: '',
    horario: '',
    pedido: contexto.pedido ?? '',
    tipoEnvio: datos?.tipoEnvio ?? '',
    transportista: datos?.transportista ?? '',
    contenedor: datos?.contenedor ?? '',
    tipoPalet: contexto.tipoPalet ?? '',
    tipoCarga: '',
    incoterm: datos?.incoterm ?? '',
    incotermPuerto: '',
    destinatario: contexto.destinatario ?? '',
    destino: contexto.destino ?? '',
    observaciones: '',
    lineas: [lineaVacia({
      bultos: numATexto(datos?.cajas),
      peso: numATexto(datos?.pesoBruto),
    })],
  }
}

/** Texto o `undefined`: un campo en blanco no se manda. */
function opt(s: string): string | undefined {
  const v = s.trim()
  return v === '' ? undefined : v
}

/** El punto de recogida con el teléfono y el correo al final.
 *
 *  El contrato no tiene campos para ellos: el punto de recogida es un único texto
 *  multilínea. */
export function componerPuntoRecogida(f: Pick<AlbaranForm, 'puntoRecogida' | 'telefono' | 'correo'>): string | undefined {
  const lineas = [
    f.puntoRecogida.trim(),
    f.telefono.trim() ? `Tel: ${f.telefono.trim()}` : '',
    f.correo.trim() ? `Email: ${f.correo.trim()}` : '',
  ].filter(Boolean)
  return lineas.length ? lineas.join('\n') : undefined
}

/** Las líneas que viajan.
 *
 *  Una línea sin nada se descarta; una con sólo texto se manda con sólo texto. La
 *  que no tiene bultos ni peso **no se convierte en ceros**: la clave se omite. */
export function lineasParaEnviar(lineas: AlbaranLineaForm[]): AlbaranRecogidaLineaEnviada[] {
  const salida: AlbaranRecogidaLineaEnviada[] = []
  for (const l of lineas) {
    const linea: AlbaranRecogidaLineaEnviada = {}
    const palets = opt(l.palets)
    const contenido = opt(l.contenido)
    const bultos = aNumero(l.bultos)
    const peso = aNumero(l.peso)
    if (palets !== undefined) linea.palets = palets
    if (bultos !== undefined) linea.bultos = bultos
    if (contenido !== undefined) linea.contenido = contenido
    if (peso !== undefined) linea.peso = peso
    if (Object.keys(linea).length > 0) salida.push(linea)
  }
  return salida
}

/** El cuerpo del POST, en snake_case.
 *
 *  **Sin totales**: el generador suma las líneas, y mandarlos sólo invitaría a que
 *  alguien creyera que mandan. El incoterm va compuesto con el puerto. */
export function cuerpoAlbaran(args: {
  empresa: string
  carga: string
  idioma: 'es' | 'en'
  form: AlbaranForm
}): AlbaranRecogidaBody {
  const { empresa, carga, idioma, form: f } = args
  const body: AlbaranRecogidaBody = { empresa, carga, idioma, lineas: lineasParaEnviar(f.lineas) }
  const puesto: Array<[keyof AlbaranRecogidaBody, string | undefined]> = [
    ['punto_recogida', componerPuntoRecogida(f)],
    ['persona_encargada', opt(f.personaEncargada)],
    ['fecha_recogida', opt(f.fechaRecogida)],
    ['horario', opt(f.horario)],
    ['pedido', opt(f.pedido)],
    ['tipo_envio', opt(f.tipoEnvio)],
    ['transportista', opt(f.transportista)],
    ['contenedor', opt(f.contenedor)],
    ['tipo_palet', opt(f.tipoPalet)],
    ['tipo_carga', opt(f.tipoCarga)],
    ['incoterm', opt(componerIncoterm(f.incoterm, f.incotermPuerto))],
    ['destinatario', opt(f.destinatario)],
    ['destino', opt(f.destino)],
    ['observaciones', opt(f.observaciones)],
  ]
  for (const [clave, valor] of puesto) {
    if (valor !== undefined) (body as unknown as Record<string, unknown>)[clave] = valor
  }
  return body
}

/** Suma de lo que hay en las líneas, sólo para enseñarla: no viaja.
 *  `null` = ninguna línea tiene ese dato (no es 0). */
export function totalesLineas(lineas: AlbaranLineaForm[]): { bultos: number | null; peso: number | null } {
  let bultos: number | null = null
  let peso: number | null = null
  for (const l of lineas) {
    const b = aNumero(l.bultos)
    const p = aNumero(l.peso)
    if (b !== undefined) bultos = (bultos ?? 0) + b
    if (p !== undefined) peso = (peso ?? 0) + p
  }
  return { bultos, peso }
}

/** Códigos de aviso que tienen que verse siempre.
 *
 *  `sin_bultos_peso` se deriva también del booleano: si el servidor mandara el
 *  flag sin el aviso, el hueco volvería a ser mudo, y un albarán con el peso en
 *  blanco sin decir por qué es justo lo que alguien firma sin mirar. */
export function avisosVisibles(datos: AlbaranRecogidaDatos | undefined): AlbaranRecogidaDatos['avisos'] {
  if (!datos) return []
  const avisos = [...datos.avisos]
  if (!datos.bultosPesoDisponibles && !avisos.some(a => a.codigo === 'sin_bultos_peso')) {
    avisos.push({ codigo: 'sin_bultos_peso', bloqueante: false })
  }
  return avisos
}
